import './Clone.scss';
import React, { Component } from 'react';
import { Switch, Layout, Card, Row, Col, Icon, Input, List, Button, Popconfirm } from 'antd';
import { getCloneAccount, getClonePaymentAccount, getCloneCaller, getCloneApplication, getHttpEnabled, getBuilderEnabled } from 'services/CeberusApiService';
import { PaymentAccountRequest } from 'models/request/PaymentAccountRequest';
import { ApplicationRequest } from 'models/request/ApplicationRequest';
import { AccountRequest } from 'models/request/AccountRequest'
import { CloneRequest } from 'models/request/CloneRequest';
import { BaseRequest } from 'models/request/BaseRequest';
import CloneService from 'services/CloneService';
import { BuilderRequest } from 'models/request/BuilderRequest';
import { PushNotification } from 'utils/PushNotification';

const { Search } = Input;
const ListItem = List.Item;
const ListItemMeta = List.Item.Meta;
const AI_DOMAIN = 'postmaster@ai.msging.net';
const AI_PROVIDER_NAME = 'DefaultProvider';
const AI_PROVIDER_VALUE = 'value';
const POPUP_CONTENT = 'Are you sure you want to clone to your bot? The selected fields on the destination bot will be replaced and the flow will need to be published.';
const DOMAIN_SEP = '@';
const FLOW_CLONING_INFORMATION = '* Attention, when cloning a flow, keep in mind that the cloned flow is the one under construction, not the one that is published.';
const DEFAULT_SWITCH_STATE = true;
const DEFAULT_IS_VALID_STATE = false;
const ConnectionType = {
  Http: 'HTTP',
  Builder: 'Builder',
  Sdk: 'SDK',
  Router: 'Router'
};

export class Clone extends Component<BaseRequest> {
  state = {
    is_loading: false,
    destination_identity: null,
    destination_information: [],
    current_application: {},
    current_application_information: [],
    is_button_disabled: true,
    cloneOptions: [
      {
        name: 'Configurations',
        cloneProcessor: CloneService.cloneConfigurationAsync,
        isValid: DEFAULT_IS_VALID_STATE,
        checked: DEFAULT_SWITCH_STATE,
        validation: CloneService.getConfigurationsAsync,
      },
      {
        name: 'Flow',
        cloneProcessor: CloneService.cloneFlowAsync,
        isValid: DEFAULT_IS_VALID_STATE,
        checked: DEFAULT_SWITCH_STATE,
        validation: CloneService.getPublishedFlowAsync,
      },
      {
        name: 'Resources',
        cloneProcessor: CloneService.cloneResourcesAsync,
        isValid: DEFAULT_IS_VALID_STATE,
        checked: DEFAULT_SWITCH_STATE,
        validation: CloneService.getResources,
      },
      {
        name: 'Global Actions',
        cloneProcessor: CloneService.cloneGlobalActionsAsync,
        isValid: DEFAULT_IS_VALID_STATE,
        checked: DEFAULT_SWITCH_STATE,
        validation: CloneService.getGlobalActionsAsync
      },
      {
        name: 'AI: Intentions',
        cloneProcessor: CloneService.cloneIntentionsAsync,
        isValid: DEFAULT_IS_VALID_STATE,
        checked: DEFAULT_SWITCH_STATE,
        validation: CloneService.getIntentions
      },
      {
        name: 'AI: Entities',
        cloneProcessor: CloneService.cloneEntitiesAsync,
        isValid: DEFAULT_IS_VALID_STATE,
        checked: DEFAULT_SWITCH_STATE,
        validation: CloneService.getEntities
      }
    ]
  };

  async componentDidMount() {
    await this.onBuild();
  }

  onChange = (checked: boolean, index: number) => {
    const { cloneOptions } = this.state;

    cloneOptions[index].checked = checked;
    this.setState({
      cloneOptions
    });
  }

  getBotInfo = async (identity: string, clusterName: string) => {
    try {
      const request = new ApplicationRequest();
      request.BotIdentifier = identity;
      request.ClusterName = clusterName;
      const { template, name } = await getCloneApplication(request);
      return { is_bot_active: true, template, name };
    } catch {
      return { is_bot_active: false, template: '-', name };
    }
  }

  getPaymentAccount = async (identity: string) => {
    try {
      const request = new PaymentAccountRequest();
      request.BotIdentifier = identity;
      const payment_account = await getClonePaymentAccount(request);
      return payment_account;
    } catch {
      return 'Cannot get Payment Account';
    }
  }

  getAIProvider = async (identity: string) => {
    const request = new CloneRequest()
    request.BotIdentifier = identity
    const { items } = await getCloneCaller(request);
    if (items) {
      const ai_conf = items.find(conf => conf.owner == AI_DOMAIN && conf.name == AI_PROVIDER_NAME);
      if (ai_conf && ai_conf[AI_PROVIDER_VALUE]) {
        return ai_conf[AI_PROVIDER_VALUE];
      }
    }
    return '-';
  }

  connectionType = async (identity: string, template: string, clusterName: string) => {
    try {
      const bot_connection = await this.getConnectionType(identity, template, clusterName);
      return bot_connection;
    } catch {
      return 'Something went wrong';
    }
  }

  getConnectionType = async (identity: string, template: string, clusterName: string) => {
    if (template === 'master')
      return ConnectionType.Router;

    const isHttp = await this.isBotHttp(identity, clusterName);

    if (isHttp !== null && isHttp)
      return ConnectionType.Http;

    const isBuilder = await this.isBotBuilder(identity, clusterName);

    if (isBuilder !== null && isBuilder)
      return ConnectionType.Builder;

    return (isHttp !== null && isBuilder !== null)
      ? ConnectionType.Sdk
      : 'Error fetching connection type';
  }

  isBotHttp = async (identity: string, clusterName: string) => {
    try {
      const request = new BuilderRequest(null, clusterName, identity, null);
      request.BotIdentifier = identity;
      const isHttp = await getHttpEnabled(request);

      return isHttp.toLowerCase() === 'true' ? true : false;
    } catch (e) {
      return null;
    }
  }

  isBotBuilder = async (identity: string, clusterName: string) => {
    try {
      const request = new BuilderRequest(null, clusterName, identity, null);
      const isBuilder = await getBuilderEnabled(request);

      return isBuilder.toLowerCase() === 'true' ? true : false;
    } catch (e) {
      return null;
    }
  }

  cloneBot = async () => {
    if (!this.state.destination_identity) {
      PushNotification.error('Chatbot not found!');
      return
    }

    try {
      this.setState({ is_loading: true });
      const originIdentity = this.props.BotIdentifier;
      const destinationIdentity = this.state.destination_identity;
      const clusterName = this.props.ClusterName;
      this.state.cloneOptions.map((resourceToClone) => {
        if (resourceToClone.isValid && resourceToClone.checked) {
          this.performCloneAsync({ resourceToClone, originIdentity, destinationIdentity, clusterName });
        }
      });

      PushNotification.success('Chatbot cloned!');
    } catch (e) {
      PushNotification.error('Something went wrong!');
    } finally {
      this.setState({ is_loading: false });
    }
  }

  performCloneAsync = async ({ resourceToClone, originIdentity, destinationIdentity, clusterName }) => {
    try {
      const cloneProcessor = resourceToClone.cloneProcessor;
      await cloneProcessor({ originIdentity, destinationIdentity, clusterName });
      PushNotification.success(`${resourceToClone.name} cloned!`);
    } catch (e) {
      PushNotification.error(`Couldn\'t clone ${resourceToClone.name}!`);
    }
  }

  getItemList = (current_application) => {
    if (current_application) {
      return [
        {
          title: 'Id:',
          description: current_application.identity != undefined
            ? current_application.identity.split(DOMAIN_SEP)[0]
            : '',
        },
        {
          title: 'Name:',
          description: current_application.full_name,
        },
        {
          title: 'Status:',
          description: current_application.is_bot_active ? 'Active' : 'Inactive'
        },
        {
          title: 'Connection Type:',
          description: current_application.connection_type,
        },
        {
          title: 'Artificial Inteligence:',
          description: current_application.ai_provider
        }
      ];
    }
    return [];
  }

  onBuild = async () => {

    this.setState({ is_loading: true });
    const originIdentity = this.props.BotIdentifier;
    const clusterName = this.props.ClusterName;
    const { is_bot_active, template, name } = await this.getBotInfo(originIdentity, clusterName);
    const aiProvider = await this.getAIProvider(originIdentity);
    const connectionType = await this.connectionType(originIdentity, template && template.toLowerCase(), clusterName);
    const identityWithoutDomain = originIdentity.split(DOMAIN_SEP)[0];

    this.setState({
      current_application:
      {
        identity: this.props.BotIdentifier,
        full_name: name,
        is_bot_active,
        connection_type: connectionType,
        ai_provider: aiProvider
      }
    });

    const { cloneOptions } = this.state;
    const availableOptions = [];

    cloneOptions.forEach(async (option) => {
      try {
        await option.validation(identityWithoutDomain, clusterName);
        option.isValid = true;
        availableOptions.push(option);
      } catch (e) {
        availableOptions.push(option);
      }
    });

    this.setState({
      current_application_information: this.getItemList(this.state.current_application),
      is_loading: false,
      cloneOptions: availableOptions
    });
  }

  onSearch = async (identifier: string) : Promise<void> => {
    if (!identifier) {
      this.setState({
        destination_information: [],
        destination_identity: null,
        is_button_disabled: true
      });
      return;
    }
    try {
      this.setState({ is_loading: true });
      await this.addApplicationByName(identifier, this.props.ClusterName);
      this.state.destination_identity = identifier;
      this.setState({ is_button_disabled: false });
    } catch (e) {
      PushNotification.error('Chatbot not found!');
      this.setState({
        destination_information: [],
        destination_identity: null,
        is_button_disabled: true
      });
    } finally {
      this.setState({ is_loading: false });
    }
  }

  addApplicationByName = async (owner_identity: string, clusterName: string) => {
    const account = await getCloneAccount(new AccountRequest(owner_identity, clusterName));
    if (account === undefined || account === "")
      throw new Error()
    try {
      this.setState({ is_loading: true });
      const payment_account = await this.getPaymentAccount(owner_identity);
      const { is_bot_active, template } = await this.getBotInfo(account.identity, clusterName);
      const connection_type = await this.getConnectionType(account.identity, template && template.toLowerCase(), clusterName);
      const ai_provider = await this.getAIProvider(account.identity);

      this.setState({
        current_application:
        {
          id: owner_identity,
          identity: account.identity,
          full_name: account.fullName,
          payment_account,
          is_bot_active,
          connection_type,
          template,
          ai_provider: ai_provider
        }
      });
      this.state.destination_information = this.getItemList(this.state.current_application);
    } finally {
      this.setState({ is_loading: false });
    }
  }

  optionSwitch = (index: number, description: string) => {
    if (this.state.cloneOptions[index].isValid) {
      return (
        <div key={index}>
          <Row>
            <Col span={18}>
              {description}
            </Col>
            <Col span={6}>
              <Switch defaultChecked onChange={(checked) => { this.onChange(checked, index) }} />
            </Col>
          </Row>
        </div>
      );
    }
  }

  render() {
    return (
      <>
        <div className="flex flex-column search">
          <h1>
            <Icon type="copy" className="mr3" />
            Clone Bot
          </h1>
          <h5>{FLOW_CLONING_INFORMATION}</h5>
          <Search
            placeholder="Destination Bot"
            enterButton="Search"
            size="large"
            onSearch={this.onSearch}
            className="w-50"
          />
        </div>
        <div className="flex flex-column cards">
          <div className="w-70">
            <Layout.Content className="flex justify-between">
              <Card title="Origin Information" className="w-30" size="small">
                <List
                  loading={this.state.is_loading}
                  itemLayout="horizontal"
                  dataSource={this.state.current_application_information}
                  renderItem={item => !item.invisible ? (
                    <ListItem actions={item.actions}>
                      <ListItemMeta
                        title={item.title}
                        description={item.description}>
                      </ListItemMeta>
                    </ListItem>
                  ) : <></>}>
                </List>
              </Card>
              <Card title="Destination Information" className="w-30" size="small">
                <List
                  loading={this.state.is_loading}
                  itemLayout="horizontal"
                  dataSource={this.state.destination_information}
                  renderItem={item => !item.invisible ? (
                    <ListItem actions={item.actions}>
                      <ListItemMeta
                        title={item.title}
                        description={item.description}>
                      </ListItemMeta>
                    </ListItem>
                  ) : <></>}>
                </List>
              </Card>
              <div className="flex flex-column switchs">
                <div className="tl">
                  {
                    this.state.cloneOptions.map((option, index) => {
                      return this.optionSwitch(index, option.name);
                    })
                  }
                </div>
                <div className="clone-button">
                  <Popconfirm disabled={this.state.is_button_disabled} placement="top" title={POPUP_CONTENT} onConfirm={this.cloneBot} okText="Clone" cancelText="Cancel">
                    <Button disabled={this.state.is_button_disabled} className='tc' size='large' type='primary'>
                      Clone
                      <Icon type="copy" />
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </Layout.Content>
          </div>
        </div>
      </>
    )
  }
}